import { ID } from '@/types/common'

export enum ClassType {
  PERSONAL = 'PERSONAL',
  SPEECH = 'SPEECH',
  SENSORY = 'SENSORY',
  GROUP = 'GROUP',
}

export enum BillingMode {
  PER_CLASS = 'PER_CLASS',
  MONTHLY = 'MONTHLY',
  ALL_DAY = 'ALL_DAY',
  GIFT = 'GIFT',
}

export enum ClassRecordKind {
  PLANNED = 'PLANNED',
  MANUAL = 'MANUAL',
  MAKEUP_USAGE = 'MAKEUP_USAGE',
}

export enum ClassRecordStatus {
  PENDING = 'PENDING',
  ATTENDED = 'ATTENDED',
  LEAVE_MAKEUP = 'LEAVE_MAKEUP',
  LEAVE_CONVERTED_TO_MONEY = 'LEAVE_CONVERTED_TO_MONEY',
  CONVERTED_TO_MONEY = 'CONVERTED_TO_MONEY',
}

export interface ClassRecord {
  id: ID
  studentId: ID
  generationBatchId?: ID | null
  classDate: string
  sessionIndex?: number
  classType: ClassType
  billingMode: BillingMode
  recordKind: ClassRecordKind
  classRecordStatus: ClassRecordStatus
  durationMinutes: number
  teacherId?: ID | null
  coursePrice?: number | null
  teacherName?: string | null
  note?: string | null
  weekday?: number
}

export interface ClassMonthlySummary {
  studentId: ID
  month: string
  plannedTrainingCount: number
  plannedGroupCount: number
  plannedAttendedTrainingCount: number
  plannedAttendedGroupCount: number
  manualAttendedTrainingCount: number
  manualAttendedGroupCount: number
  leaveMakeupTrainingCount: number
  leaveMakeupGroupCount: number
  leaveConvertedToMoneyTrainingCount: number
  leaveConvertedToMoneyGroupCount: number
  convertedToMoneyTrainingCount: number
  convertedToMoneyGroupCount: number
  makeupGeneratedMinutes: number
  makeupUsedMinutes: number
  makeupUsedGroupLessons: number
  convertedToMoneyAmount: number
  currentMakeupBalanceMinutes: number
  currentMakeupBalanceGroupLessons: number
  makeupBalanceLastRecalculatedAt?: string | null
  updatedAt?: string
}

export interface ClassRecordRules {
  id: ID | null
  makeupRetentionMonths: number
  defaultCoursePrice: number
  defaultClassDurationMinutes: number
  note?: string | null
}

export interface StudentMakeupBalance {
  studentId: ID
  currentBalanceMinutes: number
  lastRecalculatedAt: string
}

export interface ClassRecordQueryParams {
  studentId: ID
  month: string
  startDate?: string
  endDate?: string
}

export interface ClassRecordSummaryParams {
  studentId: ID
  month: string
}

export interface ClearClassRecordMonthPayload {
  studentId: ID
  month: string
}

export interface GenerateClassRecordTemplate {
  id?: ID
  classType: ClassType
  billingMode: BillingMode
  teacherId?: ID
  teacherName?: string | null
  coursePrice?: number
  sortOrder?: number
}

export interface GenerateClassRecordPayload {
  studentId: ID
  startDate: string
  endDate: string
  force?: boolean
}

export interface UpsertCurrentClassConfigPayload {
  studentId: ID
  weekdays?: number[]
  templates: GenerateClassRecordTemplate[]
  isAllDayPricing?: boolean
  monthlyPrice?: number
}

export interface UpdateClassRecordPayload {
  classDate?: string
  classType?: ClassType
  billingMode?: BillingMode
  teacherId?: ID | null
  coursePrice?: number
  classRecordStatus?: ClassRecordStatus
  durationMinutes?: number
  note?: string
}

export interface BatchUpdateClassRecordPayload {
  ids: ID[]
  classRecordStatus?: ClassRecordStatus
  teacherId?: ID
  note?: string
}

export interface StudentClassCurrentConfig {
  id: ID
  studentId: ID
  createdAt: string
  weekdays: number[]
  isAllDayPricing: boolean
  monthlyPrice?: number | null
  templates: GenerateClassRecordTemplate[]
  updatedAt: string
}

export interface StudentClassGenerationBatch {
  id: ID
  studentId: ID
  configVersionId: ID
  weekdays: number[]
  generatedMonth: string
  generatedStartDate: string
  generatedEndDate: string
  isAllDayPricing: boolean
  monthlyPrice?: number | null
  templates: GenerateClassRecordTemplate[]
  createdAt: string
}

export interface StudentClassConfigVersionGenerationBatch {
  id: ID
  studentId: ID
  configVersionId: ID
  generatedMonth: string
  generatedStartDate: string
  generatedEndDate: string
  createdAt: string
}

export interface StudentClassConfigVersion {
  id: ID
  studentId: ID
  weekdays: number[]
  isAllDayPricing: boolean
  monthlyPrice?: number | null
  isCurrent: boolean
  generationBatchCount: number
  canDelete: boolean
  deleteDisabledReason?: string | null
  templates: GenerateClassRecordTemplate[]
  generationBatches: StudentClassConfigVersionGenerationBatch[]
  createdAt: string
  updatedAt: string
}

export interface ClassRecordConfigQueryParams {
  studentId: ID
}

export interface MonthPlanSection {
  id: ID
  month: string
  generatedStartDate: string
  generatedEndDate: string
  actualStartDate?: string | null
  actualEndDate?: string | null
  weekdays: number[]
  isAllDayPricing: boolean
  monthlyPrice?: number | null
  templates: GenerateClassRecordTemplate[]
  recordCount: number
  stats: { training: number; group: number }
  templateCount: number
  createdAt: string
}

export interface MonthPlanResult {
  studentId: ID
  month: string
  totals: { training: number; group: number }
  sections: MonthPlanSection[]
}

export interface ManualCreateFromCurrentItem {
  classType: ClassType
  billingMode: BillingMode
  recordKind?: ClassRecordKind
  classRecordStatus?: ClassRecordStatus
  durationMinutes?: number
  teacherId?: ID
  teacherName?: string | null
  coursePrice?: number
  note?: string | null
}

export interface ManualCreateFromCurrentPayload {
  studentId: ID
  date: string
  items?: ManualCreateFromCurrentItem[]
}

export interface UpdateClassRecordRulesPayload {
  makeupRetentionMonths: number
  defaultCoursePrice: number
  defaultClassDurationMinutes: number
  note?: string
}
